document.addEventListener("DOMContentLoaded", () => {
  const isSearch = document.getElementById('search-overlay') !== null;
  const config = {
      overlayId: isSearch ? 'search-overlay' : 'custom-overlay',
      overlayWrapperClass: isSearch ? '.search-overlay-content-wrapper' : '.custom-overlay-content-wrapper',
      sourceClass: isSearch ? '.search-overlay-source' : '.custom-overlay-source',
      dropdownClass: '.custom-dropdown',
      closeButtonClass: '.quickview-close'
  };

  const modalCache = new Map();
  const pendingFetches = new Map();
  const prefetchQueue = [];
  let activePrefetches = 0;
  const MAX_CONCURRENT_PREFETCH = 3;

  // Overlay Handling
  initOverlay(config);
  // Bind only inside quick-view containers — this script runs site-wide, and
  // binding globally would double up on the PDP's own form/quantity handlers.
  initQuickViewContent(document);
  initCartDrawer();

  function initQuickViewContent(root) {
      const scope = root || document;
      const containers = scope.querySelectorAll(`${config.sourceClass}, #${config.overlayId}`);
      containers.forEach(container => {
          initAjaxAddToCart(config, container);
          initCustomDropdowns(config, container);
          initQuantityControls(config, container);
      });
  }

  function initOverlay(cfg) {
      const overlay = document.getElementById(cfg.overlayId);
      if (!overlay) return;
      const wrapper = overlay.querySelector(cfg.overlayWrapperClass);
      let movedNode = null, originalParent = null, nextSibling = null;

      overlay.addEventListener('click', e => {
          if (e.target === overlay) closeOverlay();
          if (e.target.closest(cfg.closeButtonClass)) closeOverlay();
      });

      // Delegated trigger — supports lazy-injected cards and works for both
      // collection (no pre-rendered source) and search (pre-rendered source).
      document.addEventListener('click', e => {
          const button = e.target.closest('[quick-view-btn]');
          if (button) openOverlay(button);
      });

      // Warm the modal cache before the click — hover on desktop, touchstart on
      // mobile. Skips the prefetch queue and fires immediately, since the click
      // is imminent.
      const prefetchFromEvent = e => {
          const card = e.target.closest('.product-card');
          if (!card) return;
          const button = card.querySelector('[quick-view-btn][data-handle]');
          if (!button) return;
          const handle = button.dataset.handle;
          if (!handle || modalCache.has(handle) || pendingFetches.has(handle)) return;
          fetchModalHtml(handle).catch(() => {});
      };
      document.addEventListener('mouseenter', prefetchFromEvent, true);
      document.addEventListener('touchstart', prefetchFromEvent, { passive: true, capture: true });

      // Background prefetch: as product cards come into view, queue their
      // modal HTML. Concurrency-limited so we don't flood the server, and
      // deferred to idle so PLP render isn't blocked.
      if ('IntersectionObserver' in window) {
          const schedule = window.requestIdleCallback
              || (cb => setTimeout(cb, 200));
          schedule(() => {
              const io = new IntersectionObserver(entries => {
                  for (const entry of entries) {
                      if (!entry.isIntersecting) continue;
                      enqueuePrefetch(entry.target.dataset.handle);
                      io.unobserve(entry.target);
                  }
              }, { rootMargin: '400px 0px' });
              document.querySelectorAll('[quick-view-btn][data-handle]').forEach(button => io.observe(button));
          }, { timeout: 2000 });
      }

      function enqueuePrefetch(handle) {
          if (!handle || modalCache.has(handle) || pendingFetches.has(handle)) return;
          if (prefetchQueue.indexOf(handle) !== -1) return;
          prefetchQueue.push(handle);
          runPrefetchQueue();
      }

      function runPrefetchQueue() {
          while (activePrefetches < MAX_CONCURRENT_PREFETCH && prefetchQueue.length) {
              const handle = prefetchQueue.shift();
              if (modalCache.has(handle) || pendingFetches.has(handle)) continue;
              activePrefetches++;
              fetchModalHtml(handle)
                  .catch(() => {})
                  .finally(() => {
                      activePrefetches--;
                      runPrefetchQueue();
                  });
          }
      }

      async function openOverlay(button) {
          const variantId = button.dataset.variant;
          const handle = button.dataset.handle;
          const preRendered = variantId
              ? document.querySelector(`${cfg.sourceClass}[data-variant="${variantId}"]`)
              : null;

          if (movedNode && originalParent) restoreNode();

          if (preRendered) {
              // Search results path: move the pre-rendered node into the overlay.
              originalParent = preRendered.parentNode;
              nextSibling = preRendered.nextSibling;
              wrapper.appendChild(preRendered);
              movedNode = preRendered;
              showOverlay();
              return;
          }

          if (!handle) return;

          // Collection page path: fetch modal HTML lazily.
          showOverlay();
          wrapper.innerHTML = '<div class="quick-view-loading" aria-live="polite">Loading…</div>';

          try {
              const html = await fetchModalHtml(handle);
              wrapper.innerHTML = html;
              initCustomDropdowns(cfg, wrapper);
              initQuantityControls(cfg, wrapper);
              initAjaxAddToCart(cfg, wrapper);
              // Re-trigger wishlist + cartx for the freshly injected variant button.
              const newVariantId = wrapper.querySelector('[product-single-form-variant-id]')?.value;
              if (newVariantId && typeof updateInventoryStates === 'function') {
                  updateInventoryStates(newVariantId);
              }
              if (typeof window.$th_refreshWishlistItems === 'function') {
                  window.$th_refreshWishlistItems();
              }
          } catch (err) {
              console.error('Quick view fetch failed', err);
              wrapper.innerHTML = '<div class="quick-view-error">Unable to load product details. Please try again.</div>';
          }
      }

      function fetchModalHtml(handle) {
          if (modalCache.has(handle)) return Promise.resolve(modalCache.get(handle));
          if (pendingFetches.has(handle)) return pendingFetches.get(handle);

          const promise = fetch(`/products/${handle}?sections=quick-view-modal`)
              .then(res => {
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  return res.json();
              })
              .then(data => {
                  const html = data['quick-view-modal'];
                  if (!html) throw new Error('Empty quick-view section response');
                  modalCache.set(handle, html);
                  return html;
              })
              .finally(() => {
                  pendingFetches.delete(handle);
              });

          pendingFetches.set(handle, promise);
          return promise;
      }

      function showOverlay() {
          overlay.classList.remove('hidden');
          document.body.classList.add('no-scroll');
      }

      function closeOverlay() {
          overlay.classList.add('hidden');
          document.body.classList.remove('no-scroll');
          if (movedNode && originalParent) {
              restoreNode();
          } else {
              wrapper.innerHTML = '';
          }
      }

      function restoreNode() {
          if (nextSibling) {
              originalParent.insertBefore(movedNode, nextSibling);
          } else {
              originalParent.appendChild(movedNode);
          }
          movedNode = null;
          originalParent = null;
          nextSibling = null;
      }
  }

  function initQuantityControls(cfg, root) {
      const scope = root || document;
      scope.querySelectorAll('[product-single-form-quantity-value]')
          .forEach(input => {
              if (input.dataset.qtyBound === 'true') return;
              const container = input.closest('.product-single__form-quantity') || input.parentNode;
              const increment = container.querySelector("[product-single-form-quantity-increment]");
              const decrement = container.querySelector("[product-single-form-quantity-decrement]");
              if (!increment || !decrement) return;

              input.dataset.qtyBound = 'true';
              decrement.disabled = parseInt(input.value, 10) <= 1;

              increment.addEventListener("click", e => {
                  e.preventDefault();
                  updateQty(input, decrement, increment, 1);
              });

              decrement.addEventListener("click", e => {
                  e.preventDefault();
                  updateQty(input, decrement, increment, -1);
              });
          });

      function updateQty(input, decrement, increment, change) {
          const max = parseInt(input.dataset.max, 10);
          let qty = parseInt(input.value, 10) + change;
          qty = Math.max(1, Math.min(max, qty));
          input.value = qty;
          decrement.disabled = qty <= 1;
          increment.disabled = qty >= max;
      }
  }

  function initAjaxAddToCart(cfg, root) {
    const scope = root || document;
    scope.querySelectorAll('form[action="/cart/add"]')
        .forEach(form => {
            if (form.dataset.qvBound === 'true') return;
            form.dataset.qvBound = 'true';
            form.addEventListener('submit', e => {
                e.preventDefault();
                addToCart(form);
            });
        });

    function addToCart(form) {
        const overlay = document.getElementById(cfg.overlayId);
        const submitBtn = form.querySelector('[product-single-form-submit]');
        const formData = new FormData(form);

        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.classList.add('loading');
        }

        fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
        })
        .then(res => {
            if (!res.ok) throw new Error('Failed to add item');
            return res.json();
        })
        .then(() => {
            overlay.classList.add('hidden');
            document.body.classList.remove('no-scroll');
            openCart();
        })
        .catch(err => {
            console.error('ATC error:', err);
        })
        .finally(() => {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.classList.remove('loading');
            }
        });
    }
  }


  function initCustomDropdowns(cfg, root) {
      const scope = root || document;
      const dropdowns = scope.querySelectorAll(cfg.dropdownClass);
      if (!dropdowns.length) return;

      if (!document.body.dataset.qvDropdownDocClick) {
          document.body.dataset.qvDropdownDocClick = 'true';
          document.addEventListener("click", () =>
              document.querySelectorAll(cfg.dropdownClass).forEach(d => d.classList.remove("open"))
          );
      }

      dropdowns.forEach(dropdown => {
          if (dropdown.dataset.qvBound === 'true') return;
          dropdown.dataset.qvBound = 'true';

          const selected = dropdown.querySelector(".custom-dropdown__selected");
          const options = dropdown.querySelectorAll(".custom-dropdown__option");
          const customIndex = dropdown.getAttribute("data-index");

          const isVariantProductsDropdown = dropdown.hasAttribute('variant-products-dropdown');

          if (isVariantProductsDropdown) {
              initVariantProductsDropdownQuickView(dropdown, selected, options, cfg);
              return;
          }

          const nativeIndex = customIndex ? customIndex.replace("custom-", "") : null;
          const nativeSelect = nativeIndex
              ? dropdown.closest(".product-single__form-option")?.querySelector(`select[data-index="${nativeIndex}"]`)
              : null;

          options.forEach(option =>
              option.addEventListener("click", e => {
                  e.stopPropagation();
                  selectOption(option);
              })
          );

          if (nativeSelect) {
              nativeSelect.addEventListener("change", () => syncNativeSelect(nativeSelect));
          }

          dropdown.addEventListener("click", e => {
              e.stopPropagation();
              document.querySelectorAll(cfg.dropdownClass).forEach(d => d !== dropdown && d.classList.remove("open"));
              dropdown.classList.toggle("open");
          });

          function renderSelectedOption(option) {
              const value = option.dataset.value;
              const colorSwatch = option.querySelector('.color-swatch');

              selected.innerHTML = '';

              if (colorSwatch) {
                  selected.appendChild(colorSwatch.cloneNode(true));
                  selected.appendChild(document.createTextNode(' ' + value));
              } else {
                  selected.textContent = value;
              }

              options.forEach(opt => opt.classList.remove("selected"));
              option.classList.add("selected");
          }

          function selectOption(option) {
              renderSelectedOption(option);
              dropdown.classList.remove("open");
              if (nativeSelect) {
                  nativeSelect.value = option.dataset.value;
                  nativeSelect.dispatchEvent(new Event("input", { bubbles: true }));
                  nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
              }
              updateVariantInfo(option);
          }

          function syncNativeSelect(select) {
              const value = select.value;
              const matchingOption = Array.from(options).find(opt => opt.dataset.value === value);
              if (matchingOption) {
                  renderSelectedOption(matchingOption);
              }
              updateVariantInfo(select);
          }
      });
  }

  function initVariantProductsDropdownQuickView(dropdown, selected, options, cfg) {
      options.forEach(option => {
          option.addEventListener("click", e => {
              e.stopPropagation();

              const productId = option.getAttribute("data-product-id");
              const variantId = option.getAttribute("data-variant-id");
              const productTitle = option.getAttribute("data-product-title");
              const productPrice = option.getAttribute("data-product-price");
              const productImage = option.getAttribute("data-product-image");
              const productAvailable = option.getAttribute("data-product-available") === "true";
              const inventoryQuantity = parseInt(option.getAttribute("data-inventory-quantity") || "0", 10);
              const inventoryPolicy = option.getAttribute("data-inventory-policy");

              const colorSwatch = option.querySelector('.color-swatch');
              const optionText = option.textContent.trim();

              selected.innerHTML = '';
              if (colorSwatch) {
                  selected.appendChild(colorSwatch.cloneNode(true));
              }
              selected.appendChild(document.createTextNode(' ' + optionText));

              options.forEach(opt => opt.classList.remove("selected"));
              option.classList.add("selected");
              dropdown.classList.remove("open");

              updateQuickViewWithVariantProduct({
                  productId,
                  variantId,
                  productTitle,
                  productPrice,
                  productImage,
                  productAvailable,
                  inventoryQuantity,
                  inventoryPolicy,
                  triggerEl: option
              });
          });
      });

      dropdown.addEventListener("click", e => {
          e.stopPropagation();
          const allDropdowns = document.querySelectorAll(cfg.dropdownClass);
          allDropdowns.forEach(d => d !== dropdown && d.classList.remove("open"));
          dropdown.classList.toggle("open");
      });
  }

  async function updateQuickViewWithVariantProduct(data) {
      const quickView = data.triggerEl.closest('.quick-view');
      if (!quickView) return;

      const titleEl = quickView.querySelector('.product-card__title');
      if (titleEl && data.productTitle) {
          titleEl.textContent = data.productTitle;
      }

      const imageEl = quickView.querySelector('.product-card__img-image');
      if (imageEl && data.productImage) {
          imageEl.src = data.productImage;
          const baseSrc = data.productImage.includes('?')
              ? data.productImage.split('?')[0]
              : data.productImage;
          imageEl.srcset = [
              `${baseSrc}?width=352 352w`,
              `${baseSrc}?width=832 832w`,
              `${baseSrc}?width=1000 1000w`
          ].join(', ');
          imageEl.alt = data.productTitle || '';
      }

      const priceEl = quickView.querySelector('.product-card__price');
      if (priceEl && data.productPrice) {
          const priceInDollars = (parseInt(data.productPrice, 10) / 100).toFixed(2);
          priceEl.innerHTML = `<span>$${priceInDollars}</span>`;
      }

    const qtyWrapper = quickView.querySelector('[product-single-form-quantity]');
    if (qtyWrapper) {
        const qtyInput = qtyWrapper.querySelector('[product-single-form-quantity-value]');
        const decBtn   = qtyWrapper.querySelector('[product-single-form-quantity-decrement]');
        const incBtn   = qtyWrapper.querySelector('[product-single-form-quantity-increment]');

        let maxQty = data.inventoryPolicy === "continue" ? 9999 : (parseInt(data.inventoryQuantity, 10) || 0);
        let remainingQty = maxQty;

        if (qtyInput) {
            qtyInput.value = 1;
            qtyInput.setAttribute("data-max", remainingQty);
        }
        if (decBtn) decBtn.disabled = true;
        if (incBtn) incBtn.disabled = remainingQty <= 1;

        const atcWrapper = quickView.querySelector('.cartx-product-button-wrap');
        if (atcWrapper) {
            const maxInventory = data.inventoryPolicy === "continue" ? 9999 : (parseInt(data.inventoryQuantity, 10) || 0);
            atcWrapper.setAttribute('data-variant-id', data.variantId);
            atcWrapper.setAttribute('data-price', data.productPrice);
            atcWrapper.setAttribute('data-inventory', maxInventory);
            atcWrapper.setAttribute('data-available', data.productAvailable ? 'true' : 'false');
        }
        if (typeof updateInventoryStates === 'function') {
            updateInventoryStates(data.variantId);
            setTimeout(() => updateInventoryStates(data.variantId), 50);
        } else {
            const atcBtn = quickView.querySelector('.cartx-add-to-cart');
            if (atcBtn) {
                if (!data.productAvailable || (data.inventoryPolicy === "deny" && remainingQty === 0)) {
                    const span = atcBtn.querySelector('span');
                    if (span) span.textContent = atcBtn.dataset.soldOutText || 'Sold out';
                    atcBtn.disabled = true;
                } else {
                    const span = atcBtn.querySelector('span');
                    if (span) span.textContent = atcBtn.dataset.addText || 'Add to bag';
                    atcBtn.disabled = false;
                }
            }
        }
    }

      const variantInput = quickView.querySelector('[product-single-form-variant-id]');
      if (variantInput && data.variantId) {
          variantInput.value = data.variantId;
      }

      const wcButton = quickView.querySelector('.wc_wl_bis_btn');
      if (wcButton) {
          wcButton.setAttribute('data-product_id', data.productId);
          wcButton.setAttribute('data-variant_id', data.variantId);
      }
      setTimeout(function () {
          const checkWidgetCount = document.querySelectorAll(`#custom-overlay .th_wl_col_btn`);
          checkWidgetCount.forEach((quickProduct) => {
            quickProduct.innerHTML = '';
            quickProduct.setAttribute('data-product_id',data.productId);
            quickProduct.setAttribute('data-variant_id',data.variantId);
          })
          // Emptying the button removes the heart; ask the wishlist app to redraw it for the new product
          if (typeof window.$th_refreshWishlistItems === 'function') {
              window.$th_refreshWishlistItems();
          }
      }, 1500);

  }


  function initCartDrawer() {
      initializeCartDrawerEvents();
  }

  // Cart Drawer Related Functions
  function initializeCartDrawerEvents() {
      const cartFormItems = document.querySelectorAll("[cart-form-item]");
      cartFormItems.forEach((item, index) => {
          const line = index + 1;
          const removeBtn = item.querySelector("[cart-form-item-remove]");
          const qtyAmount = item.querySelector("[cart-drawer-item-qty-amount]");
          const qtyPlus = item.querySelector("[cart-drawer-item-qty-plus]");
          const qtyMinus = item.querySelector("[cart-drawer-item-qty-minus]");

          if (removeBtn) {
              removeBtn.addEventListener("click", () => updateCartItem({ line, quantity: 0, sections: "cart-drawer" }));
          }

          if (qtyAmount && qtyPlus && qtyMinus) {
              qtyPlus.addEventListener("click", () => changeQty(qtyAmount, 1, line));
              qtyMinus.addEventListener("click", () => changeQty(qtyAmount, -1, line));
          }
      });
  }

  function changeQty(qtyAmount, delta, line) {
      const newQty = Math.max(0, parseInt(qtyAmount.textContent, 10) + delta);
      qtyAmount.textContent = newQty;
      updateCartItem({ line, quantity: newQty, sections: "cart-drawer" });
  }

  function updateCartItem(params) {
      const cartDrawer = document.querySelector("[cart-drawer]");
      const cartContents = document.querySelector("[cart-drawer-contents]");
      if (!cartDrawer || !cartContents) return;

      cartDrawer.classList.add("cart-drawer--loading");
      fade(cartContents, 0);

      fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params)
      })
      .then(res => res.json())
      .then(data => {
          updateHeaderCart(data.item_count);
          updateDrawerContent(data.sections["cart-drawer"]);
          fade(cartContents, 1);
          initializeCartDrawerEvents();
          window.Shopify && (window.Shopify.cart = data);
          cartDrawer.classList.remove("cart-drawer--loading");
      })
      .catch(error => {
          console.error('Cart update error:', error);
          fade(cartContents, 1);
          cartDrawer.classList.remove("cart-drawer--loading");
      });
  }

  function fade(element, opacity) {
      if (window.Ni && window.Ni.to) {
          window.Ni.to(element, { duration: 0.25, opacity });
      } else {
          element.style.opacity = opacity;
      }
  }

  function updateHeaderCart(count) {
      const header = document.querySelector("[header-cart-item-count]");
      if (header) {
          header.setAttribute("header-cart-item-count", count);
          header.textContent = count;
      }
  }

  function updateDrawerContent(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newContent = doc.querySelector("[cart-drawer-contents]");
      const cartContents = document.querySelector("[cart-drawer-contents]");
      if (newContent && cartContents) {
          cartContents.innerHTML = newContent.innerHTML;
      }
  }

  function openCart() {
      const cartDrawer = document.querySelector("[cart-drawer]");
      if (!cartDrawer) return;

      cartDrawer.classList.add("cart-drawer--loading");

      Promise.all([
          fetch("/?sections=cart-drawer").then(res => res.json()),
          fetch("/cart.js").then(res => res.json())
      ])
      .then(([sectionsData, cart]) => {
          if (!sectionsData["cart-drawer"]) throw new Error("No drawer data");

          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData["cart-drawer"], "text/html");
          const newContent = doc.querySelector("[cart-drawer-contents]").innerHTML;

          const headerCart = document.querySelector("[header-cart-item-count]");
          if (headerCart) {
              headerCart.setAttribute("header-cart-item-count", cart.item_count);
              headerCart.textContent = cart.item_count;
          }

          const drawerContents = document.querySelector("#shopify-section-cart-drawer [cart-drawer-contents]");
          if (drawerContents) {
              drawerContents.innerHTML = newContent;
              initializeCartDrawerEvents();
              cartDrawer.classList.remove("cart-drawer--loading");
              const toggle = document.querySelector("[header-cart-toggle]");
              if (toggle) {
                  toggle.setAttribute("aria-expanded", true);
                  cartDrawer.setAttribute("aria-expanded", true);
                  document.documentElement.classList.add("overflow-hidden");
              }
          }
      })
      .catch(error => {
          console.error('Open cart error:', error);
          cartDrawer.classList.remove("cart-drawer--loading");
      });
  }

  async function updateVariantInfo(triggerEl) {
    const quickView = triggerEl.closest('.quick-view') || triggerEl.closest(`${config.sourceClass}`);
    if (!quickView) return;

    const hasVariantProducts = quickView.getAttribute("data-has-variant-products") === "true";
    if (hasVariantProducts) {
        return;
    }

    const productId = quickView.getAttribute("product-id");
    if (!productId) return;

    const productJsonEl = document.querySelector(`#ProductJson-${productId}`);
    if (!productJsonEl) return;

    let productData;
    try {
        productData = JSON.parse(productJsonEl.textContent);
    } catch (e) {
        console.error("JSON parse failed", e);
        return;
    }

    const selectedOptions = Array.from(quickView.querySelectorAll(".custom-dropdown__option.selected"))
        .filter(opt => !opt.hasAttribute("data-product-id"))
        .map(opt => opt.getAttribute("data-value"));

    const variant = productData.variants.find(v =>
        v.options.every((opt, idx) => opt === selectedOptions[idx])
    );

    if (!variant) {
        console.warn("No matching variant", selectedOptions);
        return;
    }

    const variantTitle = quickView.querySelector('.product-card__title');
    if (variantTitle && variant.title) {
        variantTitle.textContent = `${productData.title} - ${variant.title}`;
    }

    const variantImage = quickView.querySelector('.product-card__img-image');
    if (variantImage && variant.featured_image && variant.featured_image.src) {
        const newImageSrc = variant.featured_image.src;
        variantImage.src = newImageSrc;

        const baseSrc = newImageSrc.includes('?')
            ? newImageSrc.split('?')[0]
            : newImageSrc;

        const newSrcset = [
            `${baseSrc}?width=352 352w`,
            `${baseSrc}?width=832 832w`,
            `${baseSrc}?width=1000 1000w`
        ].join(', ');

        variantImage.srcset = newSrcset;
        variantImage.alt = variant.title || productData.title;
    }

    const variantInput = quickView.querySelector(`[product-single-form-variant-id-${productId}]`)
                      || quickView.querySelector('[product-single-form-variant-id]');
    if (variantInput) variantInput.value = variant.id;

    const priceContainer = quickView.querySelector(".product-card__price");
    if (priceContainer) {
        priceContainer.textContent = `$${(variant.price / 100).toFixed(2)}`;
    }

    const wcButton = quickView.querySelector('.wc_wl_bis_btn');
    if (wcButton) wcButton.setAttribute('data-variant_id', variant.id);

    const qtyWrapper = quickView.querySelector('[product-single-form-quantity]');
    const qtyInput = qtyWrapper?.querySelector('[product-single-form-quantity-value]');
    const decBtn = qtyWrapper?.querySelector('[product-single-form-quantity-decrement]');
    const incBtn = qtyWrapper?.querySelector('[product-single-form-quantity-increment]');

    const atcWrapper = quickView.querySelector('.cartx-product-button-wrap');
    const atcBtn = quickView.querySelector('.cartx-add-to-cart');

    if (atcWrapper) {
        atcWrapper.setAttribute('data-variant-id', variant.id);
        atcWrapper.setAttribute('data-price', variant.price);
        atcWrapper.setAttribute('data-inventory', variant.inventory_policy === 'continue' ? 9999 : (variant.inventory_quantity || 0));
        atcWrapper.setAttribute('data-available', variant.available && (variant.inventory_policy === 'continue' || variant.inventory_quantity > 0) ? 'true' : 'false');
    }

    const inventoryPolicy = variant.inventory_policy;
    const inventoryQuantity = variant.inventory_quantity;
    let maxQty = inventoryPolicy === "continue" ? 9999 : (inventoryQuantity || 0);
    let remainingQty = maxQty;

    try {
        const res = await fetch("/cart.js");
        const cart = await res.json();
        const cartItem = cart.items.find(item => item.id === variant.id);
        if (cartItem) remainingQty = Math.max(0, maxQty - cartItem.quantity);
    } catch (err) {
        console.warn("Cart fetch error", err);
    }

    let currentQty = Math.min(parseInt(qtyInput.value, 10) || 1, remainingQty);

    qtyInput.value = currentQty;
    qtyInput.setAttribute("data-max", remainingQty);
    decBtn.disabled = currentQty <= 1;
    incBtn.disabled = currentQty >= remainingQty || remainingQty === 0;

    if (variant.inventory_policy === "deny" && remainingQty === 0) {
        atcBtn.innerHTML = atcBtn.dataset.soldOutText || "SOLD OUT";
        atcBtn.disabled = true;
    } else {
        atcBtn.innerHTML = atcBtn.dataset.addText || "Add to Bag";
        atcBtn.disabled = false;
    }
}

});
